import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../providers/auth_provider';

const CURRENCY_UNITS = [
    { value: 'USD', label: 'USD' },
    { value: 'CAD', label: 'CAD' },
];

const DURATION_UNITS = [
    { value: '1', label: '1' },
]

const PLAY_LIMIT_UNITS = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    // { value: 'all', label: 'All' },
]

export default function AddSubscription({ fetchSubscriptions }) {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        currency: 'USD',
        duration: '1',
        duration_unit: 'month',
        discount: '',
        play_limit: '',
        price_of_quantity: '1',
    });

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.price || isNaN(formData.price)) {
            newErrors.price = 'Valid price is required';
        }
        if (!formData.currency) {
            newErrors.currency = 'Currency is required';
        }
        if (!formData.duration) {
            newErrors.duration = 'Valid duration is required (Enter the number of month)';
        }
        if (!formData.price_of_quantity) {
            newErrors.price_of_quantity = 'Quantity Required';
        }
        if (formData.discount && (isNaN(formData.discount) || formData.discount < 0 || formData.discount > 100)) {
            newErrors.discount = 'Discount must be between 0 and 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for the field being changed
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    }, [errors]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the form errors');
            return;
        }

        const preparedFormData = {
            ...formData,
            discount: formData.discount === '' ? 0 : formData.discount // Default discount to 0 if empty
        };

        try {
            setLoading(true);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/plan/create`,
                preparedFormData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Signature': process.env.REACT_APP_API_SIGNATURE,
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            await fetchSubscriptions();
            toast.success('Subscription plan created successfully');
            document.getElementById('closeModal').click();

            // Reset form after successful submission
            setFormData({
                name: '',
                description: '',
                price: '',
                currency: '',
                duration: '',
                duration_unit: 'month',
                discount: '',
                play_limit: '',
                price_of_quantity: '',
                features: []
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to create subscription plan';
            toast.error(errorMessage);
            console.error('Error creating subscription:', error);
        } finally {
            setLoading(false);
        }
    }, [formData, fetchSubscriptions, token]);


    return (
        <div className="offcanvas offcanvas-end w-50" tabIndex={-1} id="offcanvasAddSubscription" aria-labelledby="offcanvasAddSubscriptionLabel">
            <div className="offcanvas-header border-bottom">
                <h5 id="offcanvasAddSubscriptionLabel" className="offcanvas-title">
                    Add Subscription Plan
                </h5>
                <button type="button" id="closeModal" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
            </div>
            <div className="offcanvas-body mx-0 flex-grow-0 h-100">
                {loading && !formData.name ? (
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form className="add-new-user pt-0" onSubmit={handleSubmit} noValidate>
                        <div className="form-floating form-floating-outline mb-4">
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                id="add-subscription-name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Name"
                                required
                            />
                            <label htmlFor="add-subscription-name">Name</label>
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <div className="form-floating form-floating-outline mb-4">
                            <textarea
                                className="form-control h-50"
                                id="add-subscription-description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Description (Optional, max 100 characters)"
                                maxLength={100}
                            />
                            <label htmlFor="add-subscription-description">Description (Optional)</label>
                        </div>

                        <div className="form-floating form-floating-outline mb-4">
                            {/* <label htmlFor="add-subscription-features">Features (Optional)</label> */}
                            <div className="features-list mt-2">
                                {(formData?.features || []).map((feature, index) => (
                                    <div key={index} className="d-flex align-items-center gap-2 mb-2">
                                        <input
                                            type="text"
                                            id={`feature-${index}`}
                                            className="form-control"
                                            placeholder={`Feature ${index + 1}`}
                                            value={feature || ''}
                                            onChange={(e) => {
                                                const newFeatures = [...(formData?.features || [])];
                                                newFeatures[index] = e.target.value;
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    features: newFeatures,
                                                }));
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => {
                                                const newFeatures = [...(formData?.features || [])];
                                                newFeatures.splice(index, 1);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    features: newFeatures,
                                                }));
                                            }}
                                            aria-label="Remove feature"
                                        >
                                            <i className="ri-delete-bin-2-fill" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm mt-2"
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            features: [...(prev?.features || []), ""],
                                        }))
                                    }
                                >
                                    <i className="ri-add-fill me-2" />
                                    Add Feature
                                </button>
                            </div>
                        </div>


                        <div className="d-flex gap-3 justify-content-between">
                            <div className="form-floating form-floating-outline mb-4 w-100">
                                <input
                                    type="number"
                                    className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                    id="add-subscription-price"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleChange}
                                    placeholder="Price"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <label htmlFor="add-subscription-price">Price</label>
                                {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                            </div>

                            <div className="w-100">
                                <div className={`form-floating form-floating-outline mb-4 ${errors.currency ? 'is-invalid' : ''}`}>
                                    <select
                                        name="currency"
                                        className="form-select form-select-sm d-block py-3"
                                        value={formData.currency}
                                        onChange={handleChange}
                                        required
                                    >
                                        {CURRENCY_UNITS.map(unit => (
                                            <option key={unit.value} value={unit.value}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.currency && <div className="invalid-feedback">{errors.currency}</div>}
                                    <label htmlFor="add-subscription-duration">Currency</label>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-3 justify-content-between">
                            <div className={`w-100 form-floating form-floating-outline mb-4 ${errors.duration ? 'is-invalid' : ''}`}>
                                <select name="duration" className="form-select form-select-sm d-block py-3" value={formData.duration} onChange={handleChange} required>
                                    {DURATION_UNITS.map(unit => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.label} Month
                                        </option>
                                    ))}
                                </select>
                                {errors.duration && <div className="invalid-feedback">{errors.duration}</div>}
                                <label htmlFor="add-subscription-duration">Duration</label>
                            </div>

                            <div className={`w-100 form-floating form-floating-outline mb-4 ${errors.play_limit ? 'is-invalid' : ''}`}>
                                <select name="play_limit" className="form-select form-select-sm d-block py-3" value={formData.play_limit} onChange={handleChange} required>
                                    <option value="">Select</option>
                                    {PLAY_LIMIT_UNITS.map(unit => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.value === '1' ? `${unit.label} Frequency` : `${unit.label} Frequencies`}
                                        </option>
                                    ))}
                                </select>
                                {errors.play_limit && <div className="invalid-feedback">{errors.play_limit}</div>}
                                <label htmlFor="add-subscription-play_limit">Number of Frequency Play Simultaneously</label>
                            </div>
                        </div>

                        <div className="form-floating form-floating-outline mb-4">
                            <select name="price_of_quantity" className="form-select form-select-sm d-block py-3" value={formData.price_of_quantity} onChange={handleChange} required>
                                {[...Array(10).keys()].map(i => (
                                    <option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </option>
                                ))}
                                <option key="all" value="all">
                                    All
                                </option>
                            </select>
                            <label htmlFor="price_of_quantity">Quantity</label>
                            {errors.price_of_quantity && <div className="invalid-feedback">{errors.price_of_quantity}</div>}
                        </div>

                        <div className="form-floating form-floating-outline mb-4">
                            <input
                                type="number"
                                className={`form-control ${errors.discount ? 'is-invalid' : ''}`}
                                id="add-subscription-discount"
                                name="discount"
                                value={formData.discount}
                                onChange={handleChange}
                                placeholder="Discount"
                                max="99"
                                min="0"
                                step="0.1"
                            />
                            <label htmlFor="add-subscription-discount">Discount (Optional)</label>
                            {errors.discount && <div className="invalid-feedback">{errors.discount}</div>}
                        </div>
                        <span className="text-danger">The number represents the percentage of discount.</span>
                        <hr />
                        <button type="submit" className="btn btn-success me-sm-3 me-1" disabled={loading}>
                            {loading ? 'Creating...' : 'Create Plan'}
                        </button>
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="offcanvas" disabled={loading}>
                            Cancel
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}